import { useEffect, useState } from "react";
import api, { formatPrice, formatApiErrorDetail, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import useUserSocket from "../lib/useUserSocket";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Repeat, CheckCircle2, XCircle, Clock, Send, CreditCard, Upload, Camera, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 4.12 (H5) — KI-Schnellbewertung des Trade-In-Fahrzeugs aus Fotos.
 * Liest die hochgeladenen Bilder + Eckdaten und ruft `/tradeins/ai-evaluate`.
 */
function AiTradeInQuickEval({ form, listing, onAcceptPrice }) {
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const isMoto = listing?.category === "motorcycle";

    const urls = ["img_front", "img_back", "img_left", "img_right", "img_dashboard", "img_damage"]
        .map((k) => form[k]).filter((v) => v && typeof v === "string");

    const run = async () => {
        if (urls.length === 0) {
            toast.info("Bitte mindestens ein Foto hochladen.");
            return;
        }
        setBusy(true);
        try {
            const body = {
                image_urls: urls,
                brand: form.brand || "",
                model: form.model || "",
                year: form.year ? Number(form.year) : null,
                mileage: form.mileage ? Number(form.mileage) : null,
            };
            const { data: resp } = await api.post("/tradeins/ai-evaluate", body);
            setData(resp);
            if (resp.mocked) {
                toast.warning("KI nicht erreichbar — Bewertung übersprungen.");
            } else {
                toast.success(`KI-Bewertung erstellt (${resp.analyzed_photos} Foto${resp.analyzed_photos !== 1 ? "s" : ""}).`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "KI-Bewertung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (!isMoto) return null; // Bicycle photos lassen wir vorerst aus

    const levelStyle = {
        "like-new": "bg-emerald-100 text-emerald-800 border-emerald-200",
        good: "bg-amber-100 text-amber-800 border-amber-200",
        rough: "bg-red-100 text-red-800 border-red-200",
    }[data?.condition] || "bg-gray-100 text-gray-700 border-gray-200";

    return (
        <div className="mt-3 bg-violet-50 border-2 border-violet-200 rounded-sm p-3" data-testid="tradein-ai-quickeval">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-violet-700" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-violet-900">KI-Schnellbewertung aus Fotos</span>
                </div>
                <button
                    type="button"
                    onClick={run}
                    disabled={busy || urls.length === 0}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-violet-700 hover:bg-violet-800 text-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    data-testid="tradein-ai-eval-btn"
                >
                    {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Analysiere…</> : <>Jetzt schätzen ({urls.length} Foto{urls.length !== 1 ? "s" : ""})</>}
                </button>
            </div>
            {data && (
                <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${levelStyle}`} data-testid="tradein-ai-condition">
                            Zustand: {data.condition}
                        </span>
                        {data.price_low_eur > 0 && (
                            <span className="font-mono-num text-sm font-bold text-violet-900" data-testid="tradein-ai-pricerange">
                                ≈ {data.price_low_eur.toLocaleString("de-DE")} – {data.price_high_eur.toLocaleString("de-DE")} €
                            </span>
                        )}
                    </div>
                    {data.rationale && (
                        <p className="text-[11px] text-gray-700 italic" data-testid="tradein-ai-rationale">„{data.rationale}"</p>
                    )}
                    {data.visible_issues?.length > 0 && (
                        <ul className="text-[11px] text-gray-700 list-disc list-inside">
                            {data.visible_issues.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    )}
                    {data.price_low_eur > 0 && data.price_high_eur > 0 && (
                        <button
                            type="button"
                            onClick={() => onAcceptPrice && onAcceptPrice(Math.round((data.price_low_eur + data.price_high_eur) / 2))}
                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-white border border-violet-600 text-violet-700 hover:bg-violet-100"
                            data-testid="tradein-ai-apply-price"
                        >
                            → Mittelwert als Zuzahlung-Ankerpreis verwenden
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

const STATUS_META = {
    pending: { label: "Wartet auf Antwort", className: "bg-amber-100 text-amber-800", icon: Clock },
    countered: { label: "Gegenvorschlag", className: "bg-blue-100 text-blue-800", icon: Repeat },
    accepted: { label: "Angenommen", className: "bg-green-100 text-green-800", icon: CheckCircle2 },
    rejected: { label: "Abgelehnt", className: "bg-red-100 text-red-800", icon: XCircle },
    withdrawn: { label: "Zurückgezogen", className: "bg-gray-100 text-gray-700", icon: XCircle },
};

export function TradeInStatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.pending;
    const Icon = meta.icon;
    return (
        <Badge className={`${meta.className} hover:${meta.className} uppercase tracking-wide font-bold rounded-sm gap-1`} data-testid="tradein-status">
            <Icon className="w-3 h-3" /> {meta.label}
        </Badge>
    );
}

const IMG_LABELS = {
    img_papers: "Fahrzeugpapiere",
    img_front: "Vorne",
    img_back: "Hinten",
    img_left: "Links",
    img_right: "Rechts",
    img_dashboard: "Tacho",
    img_damage: "Schäden",
};

const EMPTY_FORM = {
    brand: "",
    model: "",
    additional_payment: "",
    sell_only_price: "",
    message: "",
    // motorcycle
    mileage_km: "",
    tuev_until: "",
    last_service: "",
    accident_free: "yes",
    owners_count: "",
    power_kw: "",
    power_hp: "",
    // bicycle
    bike_type: "",
    frame_size: "",
    bike_condition: "",
    // images
    img_papers: "",
    img_front: "",
    img_back: "",
    img_left: "",
    img_right: "",
    img_dashboard: "",
    img_damage: "",
};

function ImageUploadTile({ field, value, onChange }) {
    const [busy, setBusy] = useState(false);
    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        } catch {
            toast.error("Upload fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };
    return (
        <label className="block cursor-pointer group" data-testid={`tradein-upload-${field}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">{IMG_LABELS[field]}</div>
            <div className={`relative aspect-square rounded-sm overflow-hidden border-2 transition-colors ${value ? "border-[#FFCC00]" : "border-dashed border-gray-300 group-hover:border-[#111827]"}`}>
                {value ? (
                    <img src={value} alt={IMG_LABELS[field]} className="w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                        {busy ? (
                            <span className="text-[10px]">Lädt…</span>
                        ) : (
                            <>
                                <Camera className="w-5 h-5 mb-1" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">optional</span>
                            </>
                        )}
                    </div>
                )}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </div>
        </label>
    );
}

export default function TradeInPanel({ listing, onUpdate }) {
    const { user } = useAuth();
    const [myTradeIn, setMyTradeIn] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const isLoggedIn = user && user !== false;
    const isOwner = isLoggedIn && user.id === listing.owner_id;
    const sellerAllows = listing.owner?.allow_tradein;
    const listingAllows = listing.allow_tradein;
    const available = listingAllows && sellerAllows && !listing.sold && listing.sale_type !== "auction";
    const isMotorcycle = listing.category === "motorcycle";

    useEffect(() => {
        if (!isLoggedIn || isOwner || !available) {
            setMyTradeIn(null);
            return;
        }
        api.get(`/listings/${listing.id}/tradeins/mine`)
            .then((r) => setMyTradeIn(r.data))
            .catch(() => setMyTradeIn(null));
    }, [listing.id, isLoggedIn, isOwner, available]);

    // Live updates: refetch on any tradein event for this listing
    useUserSocket(isLoggedIn && !isOwner && available ? user.id : null, (evt) => {
        if (!evt || !evt.type?.startsWith?.("tradein_")) return;
        if (evt.listing_id !== listing.id) return;
        api.get(`/listings/${listing.id}/tradeins/mine`)
            .then((r) => setMyTradeIn(r.data))
            .catch(() => { /* noop */ });
    });

    if (!available || isOwner) return null;

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        setError("");
        if (!isLoggedIn) { window.location.href = "/login"; return; }
        if (!form.brand.trim() || !form.model.trim()) {
            setError("Bitte Hersteller und Modell des Inzahlungsfahrzeugs angeben");
            return;
        }
        const additional = Number(form.additional_payment);
        if (!Number.isFinite(additional) || additional < 0) {
            setError("Bitte einen gültigen Zuzahlungsbetrag in € eingeben (0 erlaubt)");
            return;
        }
        const payload = {
            category: listing.category,
            brand: form.brand.trim(),
            model: form.model.trim(),
            additional_payment: additional,
            sell_only_price: form.sell_only_price ? Number(form.sell_only_price) : null,
            message: form.message.trim() || null,
        };
        if (isMotorcycle) {
            Object.assign(payload, {
                mileage_km: form.mileage_km ? Number(form.mileage_km) : null,
                tuev_until: form.tuev_until || null,
                last_service: form.last_service || null,
                accident_free: form.accident_free === "yes" ? true : (form.accident_free === "no" ? false : null),
                owners_count: form.owners_count ? Number(form.owners_count) : null,
                power_kw: form.power_kw ? Number(form.power_kw) : null,
                power_hp: form.power_hp ? Number(form.power_hp) : null,
            });
        } else {
            Object.assign(payload, {
                bike_type: form.bike_type || null,
                frame_size: form.frame_size || null,
                bike_condition: form.bike_condition || null,
            });
        }
        ["img_papers", "img_front", "img_back", "img_left", "img_right", "img_dashboard", "img_damage"].forEach((k) => {
            if (form[k]) payload[k] = form[k];
        });

        setSubmitting(true);
        try {
            const { data } = await api.post(`/listings/${listing.id}/tradeins`, payload);
            setMyTradeIn(data);
            setDialogOpen(false);
            setForm(EMPTY_FORM);
            toast.success("Inzahlungnahme-Anfrage gesendet");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const respond = async (action) => {
        try {
            const { data } = await api.post(`/tradeins/${myTradeIn.id}/${action}`);
            setMyTradeIn(data);
            toast.success(action === "accept" ? "Angenommen" : action === "reject" ? "Abgelehnt" : "Zurückgezogen");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktion fehlgeschlagen");
        }
    };

    const pay = async () => {
        try {
            const { data } = await api.post(`/tradeins/${myTradeIn.id}/pay`, { origin_url: window.location.origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zahlung konnte nicht gestartet werden");
        }
    };

    const hasOpen = myTradeIn && ["pending", "countered"].includes(myTradeIn.status);
    const finished = myTradeIn && ["accepted", "rejected", "withdrawn"].includes(myTradeIn.status);

    return (
        <div className="bg-white border-2 border-[#2563EB] rounded-sm p-5" data-testid="tradein-panel">
            <div className="flex items-center gap-2 mb-3">
                <div className="bg-[#2563EB] text-white px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Repeat className="w-3 h-3" /> Inzahlungnahme möglich
                </div>
            </div>

            {!myTradeIn && (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Der Verkäufer akzeptiert Ihr gebrauchtes {isMotorcycle ? "Motorrad" : "Fahrrad"} in Zahlung. Sie zahlen die Differenz drauf.
                    </p>
                    <Button
                        onClick={() => setDialogOpen(true)}
                        className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm h-11 uppercase tracking-wider"
                        data-testid="open-tradein-dialog"
                    >
                        <Repeat className="w-4 h-4 mr-2" /> Inzahlungnahme anbieten
                    </Button>
                </>
            )}

            {hasOpen && (
                <div className="space-y-3" data-testid="tradein-status-panel">
                    <div className="flex items-center justify-between gap-2">
                        <TradeInStatusBadge status={myTradeIn.status} />
                        <span className="text-xs text-gray-500">{formatDate(myTradeIn.updated_at)}</span>
                    </div>
                    <div className="text-sm space-y-1">
                        <div>Ihr Fahrzeug: <strong>{myTradeIn.brand} {myTradeIn.model}</strong></div>
                        <div>Ihre Zuzahlung: <strong className="font-mono-num">{formatPrice(myTradeIn.additional_payment)}</strong></div>
                    </div>
                    {myTradeIn.status === "countered" && (
                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">Gegenvorschlag des Verkäufers</div>
                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(myTradeIn.counter_additional_payment)}</div>
                            {myTradeIn.counter_message && (
                                <p className="text-xs text-gray-600 mt-2 italic">„{myTradeIn.counter_message}"</p>
                            )}
                            <div className="grid grid-cols-2 gap-2 mt-3">
                                <Button onClick={() => respond("accept")} className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-sm" data-testid="accept-tradein-counter-btn">
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Annehmen
                                </Button>
                                <Button onClick={() => respond("reject")} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 font-bold rounded-sm" data-testid="reject-tradein-counter-btn">
                                    <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                                </Button>
                            </div>
                        </div>
                    )}
                    {myTradeIn.status === "pending" && (
                        <Button variant="outline" onClick={() => respond("withdraw")} className="w-full rounded-sm border-[#111827] text-sm" data-testid="withdraw-tradein-btn">
                            Anfrage zurückziehen
                        </Button>
                    )}
                </div>
            )}

            {finished && (
                <div className="space-y-2" data-testid="tradein-finished">
                    <TradeInStatusBadge status={myTradeIn.status} />
                    {myTradeIn.status === "accepted" && (
                        <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-green-700">Vereinbarte Zuzahlung</div>
                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(myTradeIn.final_additional_payment ?? myTradeIn.additional_payment)}</div>
                            {!myTradeIn.paid && (myTradeIn.final_additional_payment ?? myTradeIn.additional_payment) > 0 && (
                                <Button
                                    onClick={pay}
                                    className="w-full mt-3 bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11 uppercase tracking-wider"
                                    data-testid="pay-tradein-btn"
                                >
                                    <CreditCard className="w-4 h-4 mr-2" /> Jetzt sicher bezahlen
                                </Button>
                            )}
                            {myTradeIn.paid && (
                                <div className="mt-3 bg-green-600 text-white p-2 rounded-sm flex items-center gap-2 text-sm font-bold">
                                    <CheckCircle2 className="w-4 h-4" /> Bezahlt – Fahrzeugübergabe abstimmen
                                </div>
                            )}
                            <p className="text-xs text-gray-600 mt-2">via Stripe · Treuhand-Zahlung an Plattform</p>
                        </div>
                    )}
                    {(myTradeIn.status === "rejected" || myTradeIn.status === "withdrawn") && (
                        <Button onClick={() => { setMyTradeIn(null); setDialogOpen(true); }} variant="outline" className="w-full rounded-sm border-[#111827]" data-testid="resubmit-tradein-btn">
                            Neue Anfrage senden
                        </Button>
                    )}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tradein-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                            <Repeat className="w-6 h-6 text-[#2563EB]" /> Inzahlungnahme anbieten
                        </DialogTitle>
                        <DialogDescription className="space-y-2 pt-2">
                            <span className="block">Für: <strong className="text-[#111827]">{listing.title}</strong></span>
                            <span className="block text-sm">Festpreis: <strong className="text-[#111827] font-mono-num">{formatPrice(listing.price)}</strong></span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Hersteller *</Label>
                                <Input value={form.brand} onChange={(e) => update("brand", e.target.value)} className="mt-1.5 rounded-sm" data-testid="tradein-brand" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Modell *</Label>
                                <Input value={form.model} onChange={(e) => update("model", e.target.value)} className="mt-1.5 rounded-sm" data-testid="tradein-model" />
                            </div>
                        </div>

                        {isMotorcycle ? (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">KM-Stand</Label>
                                    <Input type="number" value={form.mileage_km} onChange={(e) => update("mileage_km", e.target.value)} className="mt-1.5 rounded-sm font-mono-num" data-testid="tradein-km" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">TÜV bis (Monat)</Label>
                                    <Input type="month" value={form.tuev_until} onChange={(e) => update("tuev_until", e.target.value)} className="mt-1.5 rounded-sm" data-testid="tradein-tuev" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Letzte Wartung</Label>
                                    <Input type="month" value={form.last_service} onChange={(e) => update("last_service", e.target.value)} className="mt-1.5 rounded-sm" data-testid="tradein-service" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Unfallfrei?</Label>
                                    <select value={form.accident_free} onChange={(e) => update("accident_free", e.target.value)} className="mt-1.5 h-10 w-full border border-gray-300 rounded-sm px-2 bg-white text-sm" data-testid="tradein-accident">
                                        <option value="yes">Ja</option>
                                        <option value="no">Nein</option>
                                        <option value="unknown">Unbekannt</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Anzahl Vorbesitzer</Label>
                                    <Input type="number" min="1" max="20" value={form.owners_count} onChange={(e) => update("owners_count", e.target.value)} className="mt-1.5 rounded-sm" data-testid="tradein-owners" />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PS</Label>
                                        <Input type="number" value={form.power_hp} onChange={(e) => update("power_hp", e.target.value)} className="mt-1.5 rounded-sm font-mono-num" data-testid="tradein-hp" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">KW</Label>
                                        <Input type="number" value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} className="mt-1.5 rounded-sm font-mono-num" data-testid="tradein-kw" />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrradtyp</Label>
                                    <Input value={form.bike_type} onChange={(e) => update("bike_type", e.target.value)} placeholder="MTB, Rennrad…" className="mt-1.5 rounded-sm" data-testid="tradein-bike-type" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Rahmengröße</Label>
                                    <Input value={form.frame_size} onChange={(e) => update("frame_size", e.target.value)} placeholder="S/M/L oder 54" className="mt-1.5 rounded-sm" data-testid="tradein-frame" />
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Zustand</Label>
                                    <Input value={form.bike_condition} onChange={(e) => update("bike_condition", e.target.value)} placeholder="Sehr gut, wenige Gebrauchsspuren…" className="mt-1.5 rounded-sm" data-testid="tradein-bike-cond" />
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Bilder <span className="text-gray-400 normal-case font-normal">(alle optional — Sie werden ggf. per Mail erinnert)</span></div>
                            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                                {["img_papers", "img_front", "img_back", "img_left", "img_right", "img_dashboard", "img_damage"].map((f) => (
                                    <ImageUploadTile key={f} field={f} value={form[f]} onChange={(url) => update(f, url)} />
                                ))}
                            </div>
                            {/* Phase 4.12 (H5) — KI-Schnellbewertung aus Fotos */}
                            <AiTradeInQuickEval
                                form={form}
                                listing={listing}
                                onAcceptPrice={(v) => update("additional_payment", String(v))}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ihre Zuzahlung (€) *</Label>
                                <Input type="number" min="0" value={form.additional_payment} onChange={(e) => update("additional_payment", e.target.value)} placeholder="z.B. 8000" className="mt-1.5 rounded-sm font-mono-num h-12 text-lg font-bold" data-testid="tradein-additional-payment" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                    Reiner Verkauf möglich für (€)
                                </Label>
                                <Input type="number" min="0" value={form.sell_only_price} onChange={(e) => update("sell_only_price", e.target.value)} placeholder="optional" className="mt-1.5 rounded-sm font-mono-num" data-testid="tradein-sell-only" />
                                <p className="text-[11px] text-gray-500 mt-1">Falls Sie Ihr Fahrzeug alternativ nur verkaufen würden</p>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachricht (optional)</Label>
                            <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} maxLength={500} className="mt-1.5 rounded-sm min-h-[80px]" data-testid="tradein-message" />
                        </div>

                        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="tradein-error">{error}</div>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Abbrechen</Button>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm uppercase tracking-wider"
                            data-testid="tradein-submit-btn"
                        >
                            <Send className="w-4 h-4 mr-1" /> Anfrage senden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
